import { z } from 'zod';
import { IdSchema, InstantSchema } from '../common/index.js';

/**
 * Démarrage guidé d'un dossier (L64, vocaux d'Isabelle du 28/10), ajout au contrat 1.0.0 : l'avancement des étapes du catalogue
 * versionné de l'écran D17, une ligne par dossier et par étape. Une étape est faite, sans objet (avec un motif court) ou à faire
 * (jamais faite, ou rouverte) ; elle garde la réponse à sa question (étapes 12, 19 et 21). Équipe de bureau seulement.
 */
export const CodeEtapeDemarrageSchema = z
  .string()
  .regex(/^[a-z][a-z_]{1,48}$/)
  .meta({ id: 'CodeEtapeDemarrage', description: 'Code d’une étape du catalogue du démarrage guidé.' });

/** Version du catalogue (« 2026-10-30.1 »), gardée sur chaque ligne. */
export const VersionCatalogueDemarrageSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}\.\d+$/);

export const MOTIF_SANS_OBJET_MAX = 200;

export const DemarrageMarquageSchema = z
  .object({
    etat: z.enum(['fait', 'sans_objet']).nullable(),
    motif: z.string().trim().max(MOTIF_SANS_OBJET_MAX).optional(),
    versionCatalogue: VersionCatalogueDemarrageSchema,
  })
  .refine((m) => m.etat !== 'sans_objet' || (m.motif ?? '').trim() !== '', {
    message: 'Une étape sans objet porte un motif.',
    path: ['motif'],
  })
  .meta({ id: 'DemarrageMarquage', description: 'Marquer une étape faite, sans objet (avec motif), ou la rouvrir (null).' });

/** Couloir G, point 4 : cocher (ou décocher) une condition de passage qui ne s'évalue pas sur les données du dossier. */
export const DemarrageCocheSchema = z
  .object({ codeCondition: CodeEtapeDemarrageSchema, coche: z.boolean(), versionCatalogue: VersionCatalogueDemarrageSchema })
  .meta({ id: 'DemarrageCoche', description: 'Cocher ou décocher une condition de passage d’une étape.' });

export const DemarrageReponseSchema = z
  .object({ reponse: CodeEtapeDemarrageSchema, versionCatalogue: VersionCatalogueDemarrageSchema })
  .meta({ id: 'DemarrageReponse', description: 'Réponse à la question d’une étape (code d’option du catalogue).' });

export const DemarrageEtapeSchema = z
  .object({
    codeEtape: CodeEtapeDemarrageSchema,
    versionCatalogue: VersionCatalogueDemarrageSchema,
    etat: z.enum(['fait', 'sans_objet']).nullable(),
    faitLe: InstantSchema.nullable(),
    faitPar: z.string().nullable(),
    reponse: CodeEtapeDemarrageSchema.nullable(),
    motif: z.string().nullable(),
    /** Couloir G, point 4 : conditions de passage cochées à la main (codes du catalogue). */
    conditionsCochees: z.array(CodeEtapeDemarrageSchema).optional(),
  })
  .refine((e) => (e.etat === null ? e.faitLe === null : e.faitLe !== null), {
    message: 'Une étape faite ou sans objet porte sa date ; une étape à faire n’en porte pas.',
  })
  .meta({ id: 'DemarrageEtape' });

/**
 * Couloir G, point 5 (décision de Joel du 30/10, journal/decisions.md) : « jamais bloqué ». Une condition manquante n'arrête plus
 * le travail ; seule la dirigeante (ou l'administration) peut forcer, avec un motif, et le forçage est tracé : qui, quand, motif et
 * conditions manquantes. Portée « etape » : une étape du démarrage guidé (elle devient faite) ; portée « mandat_recherche » : le
 * blocage « mandat signé nécessaire » de l'arbre, levé pour ce dossier. Une trace n'est jamais modifiée ni supprimée.
 */
export const PorteeForcageSchema = z.enum(['etape', 'mandat_recherche']).meta({ id: 'PorteeForcage' });
export const MOTIF_FORCAGE_MAX = 300;
const ConditionsManquantesSchema = z.array(z.string().trim().min(1).max(200)).min(1).max(10);

export const ForcageCreateSchema = z
  .object({
    portee: PorteeForcageSchema,
    codeEtape: CodeEtapeDemarrageSchema.optional(),
    versionCatalogue: VersionCatalogueDemarrageSchema.optional(),
    conditions: ConditionsManquantesSchema,
    motif: z.string().trim().min(3).max(MOTIF_FORCAGE_MAX),
  })
  .refine((f) => f.portee !== 'etape' || (f.codeEtape !== undefined && f.versionCatalogue !== undefined), {
    message: 'Le forçage d’une étape porte son code et la version du catalogue.',
    path: ['codeEtape'],
  })
  .meta({ id: 'ForcageCreate', description: 'Forcer une étape ou le blocage du mandat, avec un motif (dirigeante seulement).' });

export const ForcageSchema = z
  .object({
    id: IdSchema,
    portee: PorteeForcageSchema,
    codeEtape: CodeEtapeDemarrageSchema.nullable(),
    conditions: z.array(z.string().min(1)),
    motif: z.string().min(1),
    forcePar: z.string().min(1),
    forceLe: InstantSchema,
  })
  .meta({ id: 'Forcage' });

export const DemandeDeblocageCreateSchema = z
  .object({ portee: PorteeForcageSchema, codeEtape: CodeEtapeDemarrageSchema.optional(), conditions: ConditionsManquantesSchema })
  .refine((d) => d.portee !== 'etape' || d.codeEtape !== undefined, { message: 'La demande pour une étape porte son code.', path: ['codeEtape'] })
  .meta({ id: 'DemandeDeblocageCreate', description: 'Demander à la dirigeante de débloquer une étape ou le mandat (une tâche lui est créée).' });

export const DemandeDeblocageSchema = z
  .object({ tacheId: IdSchema, prevenue: z.string().min(1) })
  .meta({ id: 'DemandeDeblocage', description: 'Tâche créée pour la dirigeante, et son nom.' });

export const DemarrageAvancementSchema = z
  .object({ dossierId: IdSchema, etapes: z.array(DemarrageEtapeSchema), forcages: z.array(ForcageSchema).optional() })
  .meta({ id: 'DemarrageAvancement', description: 'Avancement du démarrage guidé d’un dossier (équipe de bureau).' });

export type DemarrageAvancement = z.infer<typeof DemarrageAvancementSchema>;

/**
 * Couloir G, point 1 : mode de travail choisi après la connexion par Isabelle (dirigeante ou administration) et Brigitte, gardé
 * par utilisateur. « avance » : l'application actuelle ; « guide » : le poste de pilotage simple (écran D18). Null : pas encore choisi.
 */
export const ModeTravailSchema = z.enum(['guide', 'avance']).meta({ id: 'ModeTravail' });
export const ModeTravailChoixSchema = z.object({ mode: ModeTravailSchema }).meta({ id: 'ModeTravailChoix' });
export const ModeTravailLuSchema = z.object({ mode: ModeTravailSchema.nullable() }).meta({ id: 'ModeTravailLu' });

/** Tableau du mode guidé : les dossiers en cours (prospect, actif, bloqué) et l'avancement de leur démarrage guidé. */
export const TableauGuideSchema = z
  .object({
    dossiers: z.array(z.object({ dossierId: IdSchema, reference: z.string(), titre: z.string(), etapes: z.array(DemarrageEtapeSchema) })),
  })
  .meta({ id: 'TableauGuide', description: 'Dossiers en cours et avancement de leur démarrage guidé (équipe de bureau).' });
export type DemarrageEtape = z.infer<typeof DemarrageEtapeSchema>;
export type Forcage = z.infer<typeof ForcageSchema>;

/**
 * Couloir G, point 6 : vocal de l'entretien d'accueil (étape 1), déposé au dossier par morceaux (le corps JSON de l'API est limité
 * à 1 Mo). Gardé en base, équipe MAAT seulement. Transcrit par le service configuré (texte toujours « à vérifier ») ; sans service,
 * il reste déposé au statut « transcription_en_attente ».
 */
export const VOCAL_TAILLE_MAX = 25 * 1024 * 1024;
export const VOCAL_MORCEAU_MAX_BASE64 = 700_000;
export const StatutVocalSchema = z.enum(['televersement', 'transcription_en_attente', 'transcrit', 'echec_transcription']).meta({ id: 'StatutVocal' });
export const VocalCreateSchema = z
  .object({
    nomFichier: z.string().trim().min(1).max(200),
    mime: z.string().regex(/^audio\/[a-z0-9.+-]+(;.*)?$/i),
    taille: z.number().int().min(1).max(VOCAL_TAILLE_MAX),
  })
  .meta({ id: 'VocalCreate', description: 'Ouvrir le dépôt d’un vocal (fichier audio) au dossier.' });
export const VocalMorceauSchema = z
  .object({ index: z.number().int().min(0).max(100), donnees: z.string().min(1).max(VOCAL_MORCEAU_MAX_BASE64).regex(/^[A-Za-z0-9+/]+=*$/) })
  .meta({ id: 'VocalMorceau', description: 'Un morceau du fichier, en base64, dans l’ordre.' });
export const VocalSchema = z
  .object({
    id: IdSchema,
    nomFichier: z.string(),
    mime: z.string(),
    taille: z.number().int(),
    recus: z.number().int(),
    statut: StatutVocalSchema,
    transcription: z.string().nullable(),
    deposePar: z.string(),
    deposeLe: InstantSchema,
  })
  .meta({ id: 'Vocal', description: 'Vocal déposé au dossier ; la transcription est « à vérifier ».' });
